import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, Clock, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { gymInfo } from '../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בשליחת הטופס. אנא נסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-page relative z-10">
      <div className="container mx-auto px-5 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="heading-2 mb-6">צור קשר</h2>
          <p className="body-medium text-secondary max-w-2xl mx-auto">
            יש לך שאלות? נשמח לעזור ולספק לך את כל המידע
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="contact-form-wrapper scroll-reveal-left">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="form-label">
                  שם מלא
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="form-input"
                  placeholder="הכנס את שמך"
                />
              </div>

              <div>
                <label htmlFor="email" className="form-label">
                  אימייל
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="form-input"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="form-label">
                  טלפון
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="form-input"
                  placeholder="050-123-4567"
                />
              </div>

              <div>
                <label htmlFor="message" className="form-label">
                  הודעה
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  rows="5"
                  className="form-input resize-none"
                  placeholder="ספר לנו איך נוכל לעזור..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span>שולח...</span>
                    <div className="spinner"></div>
                  </>
                ) : (
                  <>
                    <span>שלח הודעה</span>
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8 scroll-reveal-right">
            {/* Address */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="heading-5 text-primary mb-2">כתובת</h3>
                  <p className="body-small text-secondary">
                    {gymInfo.address}
                  </p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <h3 className="heading-5 text-primary mb-2">טלפון</h3>
                  <a 
                    href={`tel:${gymInfo.phone}`}
                    className="body-small text-wood-lighter hover:text-wood-light transition-colors"
                  >
                    {gymInfo.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="heading-5 text-primary mb-2">אימייל</h3>
                  <a 
                    href={`mailto:${gymInfo.email}`}
                    className="body-small text-wood-lighter hover:text-wood-light transition-colors"
                  >
                    {gymInfo.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="contact-icon">
                  <Clock size={24} />
                </div>
                <div className="w-full">
                  <h3 className="heading-5 text-primary mb-3">שעות פעילות</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="body-small text-secondary">ראשון-חמישי</span>
                      <span className="body-small text-primary">6:30 - 23:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="body-small text-secondary">שישי</span>
                      <span className="body-small text-primary">7:00 - 19:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="body-small text-secondary">שבת</span>
                      <span className="body-small text-primary">9:00 - 13:00, 16:00 - 22:00</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="contact-icon">
                  <Instagram size={24} />
                </div>
                <div>
                  <h3 className="heading-5 text-primary mb-2">עקבו אחרינו</h3>
                  <a 
                    href={gymInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="body-small text-wood-lighter hover:text-wood-light transition-colors"
                  >
                    @hof.zy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-16 scroll-reveal">
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3355.8867891234567!2d34.9531!3d32.5694!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151db9e2a2a2a2a2%3A0x1234567890abcdef!2sHa-Meyasdim%20St%2051%2C%20Zikhron%20Ya'akov!5e0!3m2!1sen!2sil!4v1234567890123"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="House of Fitness Location"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
